import { EVOLUTION_MODEL, volumetricLwc } from '../evolution.ts';
import {
  cellCenterX,
  cellCenterY,
  expandBlocks,
  type GridSpec,
  type ScalarField,
} from '../grid.ts';
import type { GridPlan } from '../resolution.ts';
import type { SnowpackState, TerrainState } from '../scene.ts';
import { Grain, WEAK_LAYER_GRAINS, type SnowColumns } from '../snowpack.ts';
import { FOREST_MODEL } from '../forest.ts';
import { columnStability, STABILITY_MODEL, wetSurfaceDepth } from '../stability.ts';
import { sizeClass, type SnowFlowType } from './params.ts';

/**
 * What slides: turning a trigger into released snow per terrain cell, plus what the flow may
 * entrain and the bed it runs on.
 *
 * - Brush: every terrain cell within the radius that is steep enough releases, down to the
 *   chosen depth (the slab above the weakest layer, the new snow, the full snowpack, or a fixed
 *   depth).
 * - Natural trigger: starting from the clicked cell, a fracture in the critical weak layer
 *   spreads to connected cells with the same failure layer that are unstable and propagation-
 *   prone, up to a maximum distance; the slab above that layer releases. Without a slab to
 *   release, a wet surface layer on a steep slope goes as a wet loose avalanche: a point release
 *   of the wet snow in the clicked cell, growing by entrainment on the way down.
 */

export type ReleaseDepthMode = 'slab' | 'newSnow' | 'full' | 'fixed';

export type ReleaseTrigger =
  | {
      kind: 'brush';
      /** UTM centre and radius (m). */
      x: number;
      y: number;
      radius: number;
      depthMode: ReleaseDepthMode;
      /** For depthMode 'fixed' (cm). */
      fixedCm: number;
    }
  | {
      kind: 'natural';
      x: number;
      y: number;
      /** Upper bound on how far the fracture may spread (m). */
      maxPropagation: number;
    };

export const RELEASE_MODEL = {
  /** Terrain steeper than this can release. */
  minSlope: 28,
  maxSlope: 60,
  /** Natural trigger: the seed must be at least this unstable (SK38). */
  seedMaxSkier: STABILITY_MODEL.fair,
  /** Natural trigger: cells the fracture spreads into must be at least this propagation-prone. */
  minPropagation: 0.3,
  /** Default fracture reach (m) by failure layer: persistent layers propagate much further. */
  defaultReach: { persistent: 300, other: 100 },
  /** Released snow counts as wet above this mean volumetric LWC. */
  wetLwc: 0.01,
} as const;

export interface ReleaseResult {
  /** Terrain cells that release. */
  cells: Int32Array;
  /** Released snow per terrain cell (kg/m²), full terrain grid. */
  release: Float32Array;
  /** Density of the released snow per terrain cell (kg/m³). */
  releaseDensity: Float32Array;
  /** Snowpack cells that release (for applying the result back to the snowpack). */
  snowpackCells: Int32Array;
  flowType: SnowFlowType;
  areaM2: number;
  volumeM3: number;
  massKg: number;
  meanDepth: number;
  /** EAWS size estimated from the release volume (the deposit is usually larger). */
  sizeClass: 1 | 2 | 3 | 4 | 5;
  /** Failure layer grain for slab releases (natural / slab mode), when uniform. */
  failureGrain: Grain | null;
  /** A loose-snow (wet loose) point release rather than a slab: cohesionless, entrains easily. */
  loose: boolean;
  /** Why nothing releases, when `cells` is empty. */
  reason: string | null;
}

/** Released mass (kg/m²), its thickness (m) and water (kg/m²) for one snowpack cell. */
interface CellRelease {
  mass: number;
  thickness: number;
  water: number;
  failureGrain: Grain | null;
}

function releaseForCell(
  snow: SnowpackState,
  cell: number,
  mode: ReleaseDepthMode,
  fixedCm: number,
): CellRelease {
  const cols = snow.columns;
  const n = cols.count[cell]!;
  const base = cell * cols.maxLayers;
  const none: CellRelease = { mass: 0, thickness: 0, water: 0, failureGrain: null };
  if (n === 0) return none;
  if (mode === 'fixed') return topDepth(cols, cell, fixedCm / 100);
  // Layers with index ≥ stopIndex release.
  let stopIndex = 0;
  let failureGrain: Grain | null = null;
  if (mode === 'slab') {
    const cs = columnStability(
      cols,
      cell,
      snow.topo.slope.data[cell]!,
      snow.airTemp[cell]!,
      snow.evaluationHour,
    );
    if (cs.critical < 0) return none;
    const failure = cs.layers[cs.critical]!.layer - base;
    stopIndex = failure + 1;
    failureGrain = cols.grain[base + failure]! as Grain;
  } else if (mode === 'newSnow') {
    stopIndex = n;
    while (stopIndex > 0 && cols.depositedAt[base + stopIndex - 1]! >= 0) stopIndex--;
  }
  let mass = 0;
  let thickness = 0;
  let water = 0;
  for (let k = stopIndex; k < n; k++) {
    mass += cols.mass[base + k]!;
    thickness += cols.mass[base + k]! / cols.density[base + k]!;
    water += cols.water[base + k]!;
  }
  return { mass, thickness, water, failureGrain };
}

function topDepth(cols: SnowColumns, cell: number, depth: number): CellRelease {
  const base = cell * cols.maxLayers;
  let mass = 0;
  let thickness = 0;
  let water = 0;
  for (let k = cols.count[cell]! - 1; k >= 0 && thickness < depth; k--) {
    const h = cols.mass[base + k]! / cols.density[base + k]!;
    const take = Math.min(h, depth - thickness);
    const f = h > 0 ? take / h : 0;
    mass += cols.mass[base + k]! * f;
    water += cols.water[base + k]! * f;
    thickness += take;
  }
  return { mass, thickness, water, failureGrain: null };
}

/** Snowpack cell containing a terrain cell. */
function snowpackCellOf(plan: GridPlan, terrainCell: number): number {
  const k = plan.snowpack.cellSize / plan.terrain.cellSize;
  const c = Math.floor((terrainCell % plan.terrain.cols) / k);
  const r = Math.floor(Math.floor(terrainCell / plan.terrain.cols) / k);
  return r * plan.snowpack.cols + c;
}

export function computeRelease(
  terrain: TerrainState,
  snow: SnowpackState,
  trigger: ReleaseTrigger,
  /** Crown cover (0–1) on the terrain grid; dense forest doesn't release (sim/forest.ts). */
  forestCover: Float32Array | null = null,
): ReleaseResult {
  const plan = terrain.plan;
  const t = plan.terrain;
  const nT = t.cols * t.rows;
  const release = new Float32Array(nT);
  const releaseDensity = new Float32Array(nT).fill(200);
  const slope = terrain.topo.slope.data;
  const cellArea = t.cellSize * t.cellSize;

  // Snowpack cells that release, with their per-cell release.
  const perSnowCell = new Map<number, CellRelease>();
  let reason: string | null = null;
  let loose = false;

  if (trigger.kind === 'brush') {
    for (let r = 0; r < t.rows; r++) {
      const dy = cellCenterY(t, r) - trigger.y;
      if (Math.abs(dy) > trigger.radius) continue;
      for (let c = 0; c < t.cols; c++) {
        const dx = cellCenterX(t, c) - trigger.x;
        if (dx * dx + dy * dy > trigger.radius * trigger.radius) continue;
        const i = r * t.cols + c;
        const sl = slope[i]!;
        if (sl < RELEASE_MODEL.minSlope || sl > RELEASE_MODEL.maxSlope) continue;
        const sc = snowpackCellOf(plan, i);
        if (!perSnowCell.has(sc)) {
          perSnowCell.set(sc, releaseForCell(snow, sc, trigger.depthMode, trigger.fixedCm));
        }
      }
    }
    if (perSnowCell.size === 0)
      reason = `No slopes of ${RELEASE_MODEL.minSlope}° or steeper under the brush.`;
  } else {
    const seed = cellAtUtm(plan.snowpack, trigger.x, trigger.y);
    const seedResult = seed < 0 ? null : naturalSeed(snow, seed);
    const wetLoose = seed < 0 || seedResult ? 0 : wetLooseDepth(snow, seed);
    if (wetLoose > 0) {
      perSnowCell.set(seed, topDepth(snow.columns, seed, wetLoose));
      loose = true;
    } else if (!seedResult) {
      reason = 'The snowpack here is stable enough not to release (or there is no slab).';
    } else {
      for (const sc of floodFill(snow, seed, seedResult.grain, trigger.maxPropagation)) {
        perSnowCell.set(sc, releaseForCell(snow, sc, 'slab', 0));
      }
    }
  }

  // Map to terrain cells.
  const cells: number[] = [];
  const snowCells: number[] = [];
  let mass = 0;
  let volume = 0;
  let water = 0;
  let thicknessSum = 0;
  let failureGrain: Grain | null | undefined;
  let forestHeld = 0;
  const k = plan.snowpack.cellSize / plan.terrain.cellSize;
  for (const [sc, rel] of perSnowCell) {
    if (!(rel.mass > 0)) continue;
    snowCells.push(sc);
    const rho = rel.mass / rel.thickness;
    const sr = Math.floor(sc / plan.snowpack.cols);
    const scc = sc % plan.snowpack.cols;
    for (let dr = 0; dr < k; dr++) {
      for (let dc = 0; dc < k; dc++) {
        const i = (sr * k + dr) * t.cols + (scc * k + dc);
        if (slope[i]! < RELEASE_MODEL.minSlope - 3) continue;
        if (forestCover && forestCover[i]! >= FOREST_MODEL.releaseMaxCover) {
          forestHeld++;
          continue;
        }
        if (trigger.kind === 'brush') {
          const dx = cellCenterX(t, scc * k + dc) - trigger.x;
          const dy = cellCenterY(t, sr * k + dr) - trigger.y;
          if (dx * dx + dy * dy > trigger.radius * trigger.radius) continue;
          if (slope[i]! < RELEASE_MODEL.minSlope) continue;
        }
        release[i] = rel.mass;
        releaseDensity[i] = rho;
        cells.push(i);
        mass += rel.mass * cellArea;
        volume += rel.thickness * cellArea;
        water += rel.water * cellArea;
        thicknessSum += rel.thickness;
      }
    }
    if (failureGrain === undefined) failureGrain = rel.failureGrain;
    else if (failureGrain !== rel.failureGrain) failureGrain = null;
  }
  if (cells.length === 0 && !reason && forestHeld > 0) {
    reason = 'Dense forest anchors the snow here — nothing releases.';
  }
  if (cells.length === 0 && !reason) {
    reason =
      trigger.kind === 'brush' && trigger.depthMode === 'slab'
        ? 'No weak layer under a slab here — try "new snow" or "full depth".'
        : 'No snow to release here.';
  }
  const lwc = volume > 0 ? water / 1000 / volume : 0;
  return {
    cells: Int32Array.from(cells),
    release,
    releaseDensity,
    snowpackCells: Int32Array.from(snowCells),
    flowType: lwc > RELEASE_MODEL.wetLwc ? 'wet' : 'dry',
    areaM2: cells.length * cellArea,
    volumeM3: volume,
    massKg: mass,
    meanDepth: cells.length > 0 ? thicknessSum / cells.length : 0,
    sizeClass: sizeClass(volume),
    failureGrain: failureGrain ?? null,
    loose,
    reason,
  };
}

function cellAtUtm(spec: GridSpec, x: number, y: number): number {
  const c = Math.floor((x - spec.minX) / spec.cellSize);
  const r = Math.floor((spec.maxY - y) / spec.cellSize);
  if (c < 0 || r < 0 || c >= spec.cols || r >= spec.rows) return -1;
  return r * spec.cols + c;
}

function naturalSeed(snow: SnowpackState, seed: number): { grain: Grain } | null {
  const st = snow.stability;
  const sl = snow.topo.slope.data[seed]!;
  if (sl < RELEASE_MODEL.minSlope || sl > RELEASE_MODEL.maxSlope) return null;
  if (st.criticalGrain[seed] === 255 || st.skier.data[seed]! >= RELEASE_MODEL.seedMaxSkier)
    return null;
  return { grain: st.criticalGrain[seed]! as Grain };
}

/** Depth (m) of wet surface snow that can go as a wet loose avalanche from this cell, or 0. */
function wetLooseDepth(snow: SnowpackState, cell: number): number {
  const sl = snow.topo.slope.data[cell]!;
  if (sl < RELEASE_MODEL.minSlope || sl > RELEASE_MODEL.maxSlope) return 0;
  return wetSurfaceDepth(snow.columns, cell);
}

/** Connected snowpack cells the fracture spreads to (8-neighbour flood fill from the seed). */
function floodFill(
  snow: SnowpackState,
  seed: number,
  grain: Grain,
  maxPropagation: number,
): number[] {
  const spec = snow.spec;
  const st = snow.stability;
  const slope = snow.topo.slope.data;
  const persistent = WEAK_LAYER_GRAINS.has(grain) && grain !== Grain.PPgp;
  const reach = Math.min(
    maxPropagation,
    persistent ? RELEASE_MODEL.defaultReach.persistent : RELEASE_MODEL.defaultReach.other,
  );
  const sx = cellCenterX(spec, seed % spec.cols);
  const sy = cellCenterY(spec, Math.floor(seed / spec.cols));
  const visited = new Uint8Array(spec.cols * spec.rows);
  const out: number[] = [];
  const stack = [seed];
  visited[seed] = 1;
  while (stack.length > 0) {
    const i = stack.pop()!;
    out.push(i);
    const c = i % spec.cols;
    const r = Math.floor(i / spec.cols);
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nc = c + dc;
        const nr = r + dr;
        if (nc < 0 || nr < 0 || nc >= spec.cols || nr >= spec.rows) continue;
        const j = nr * spec.cols + nc;
        if (visited[j]) continue;
        visited[j] = 1;
        const sl = slope[j]!;
        if (sl < RELEASE_MODEL.minSlope || sl > RELEASE_MODEL.maxSlope) continue;
        if (st.criticalGrain[j] !== grain) continue;
        if (st.skier.data[j]! >= RELEASE_MODEL.seedMaxSkier) continue;
        if (st.propagation.data[j]! < RELEASE_MODEL.minPropagation) continue;
        const dx = cellCenterX(spec, nc) - sx;
        const dy = cellCenterY(spec, nr) - sy;
        if (dx * dx + dy * dy > reach * reach) continue;
        stack.push(j);
      }
    }
  }
  return out;
}

/** What the flow can pick up: snow deposited during the scenario (storm snow, wind slab). */
export interface EntrainmentInput {
  entrainable: Float32Array;
  entrainableDensity: Float32Array;
}

export function entrainableSnow(
  plan: GridPlan,
  snow: SnowpackState,
  release: ReleaseResult,
): EntrainmentInput {
  const sp = plan.snowpack;
  const cols = snow.columns;
  const mass = new Float32Array(sp.cols * sp.rows);
  const density = new Float32Array(sp.cols * sp.rows).fill(150);
  for (let i = 0; i < mass.length; i++) {
    const base = i * cols.maxLayers;
    let m = 0;
    let h = 0;
    for (let k = cols.count[i]! - 1; k >= 0; k--) {
      // Snow from the scenario, and wet surface snow (wet loose avalanches grow in it) — not the
      // settled old snowpack beneath.
      const young = cols.depositedAt[base + k]! >= EVOLUTION_MODEL.youngAfterHour;
      if (!young && volumetricLwc(cols, base + k) < STABILITY_MODEL.wetLooseLwc) break;
      m += cols.mass[base + k]!;
      h += cols.mass[base + k]! / cols.density[base + k]!;
    }
    mass[i] = m;
    if (h > 0) density[i] = m / h;
  }
  const t = plan.terrain;
  const entrainable = expandBlocks({ spec: sp, data: mass }, t).data;
  const entrainableDensity = expandBlocks({ spec: sp, data: density }, t).data;
  // Released cells have no snow left to entrain.
  for (const i of release.cells) entrainable[i] = 0;
  return { entrainable, entrainableDensity };
}

/** Bed the flow runs on: terrain + snow depth (on the terrain grid) − released depth. */
export function flowBed(
  terrain: TerrainState,
  snowDepthOnTerrain: ScalarField,
  release: ReleaseResult,
): Float32Array {
  const bed = new Float32Array(terrain.elevation.data.length);
  for (let i = 0; i < bed.length; i++) {
    bed[i] = terrain.elevation.data[i]! + Math.max(0, snowDepthOnTerrain.data[i]!);
  }
  for (const i of release.cells) {
    bed[i] = bed[i]! - release.release[i]! / release.releaseDensity[i]!;
  }
  return bed;
}
